import {
    Box,
    Checkbox,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import JsonView from "@uiw/react-json-view";
import {AuditLogsQueryResult, useAuditLogsQuery} from "./queries.generated";
import {ReactNode, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {GenericPaginator, HeaderWithOrdering, LoadingIcon} from "@dvrt/graphql-widgets";
import {Link} from "@/components/widgets";
import {defineMessage, FormattedMessage} from "react-intl";
import {DateTime} from "luxon";
import {useSortingWithCompatibility} from "./utils";
import {useDebounceState, usePaginatedQuery} from "@/hooks";
import LinkWithMagnifier from "@/components/LinkWithMagnifier";

type ToggleSystemWideFilterProps = {
    checked: boolean;
    onChange: (checked: boolean) => void;
};

const ToggleSystemWideFilter = ({checked, onChange}: ToggleSystemWideFilterProps) => {
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <Typography variant="body2">
                <FormattedMessage id="audit.system_events_only" defaultMessage="System events only" />
            </Typography>
            <Checkbox checked={checked} onChange={(event) => onChange(event.target.checked)} color="primary" />
        </Box>
    );
};

const NumberOrActorFilter = ({
    value,
    onChange,
}: {
    value: string | undefined;
    onChange: (value: string | undefined) => void;
}) => {
    return (
        <TextField
            variant="standard"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            label={<FormattedMessage id="audit.number_or_actor" defaultMessage="Number or actor" />}
        />
    );
};

const DomainName = ({name, id}: {name: string; id: number}) => {
    if (!name) return null;
    return <Link to={`/domains/${id}`}>{name}</Link>;
};

const ItemName = ({shortNumber, e164}: {shortNumber: string; e164?: string}) => {
    if (!shortNumber) return null;
    let name = shortNumber;
    if (e164) {
        name += ` (${e164})`;
    }

    return name;
};

const ActorLink = ({actor}: {actor: string}) => {
    const queryParams = new URLSearchParams();
    queryParams.set("filters[column]" as const, "free" as const);
    queryParams.set("filters[value]" as const, `%${actor}%`);
    queryParams.set("filters[operator]" as const, "ilike" as const);

    return (
        <LinkWithMagnifier to={`${window.heimdallBaseUrl}/users/list?${queryParams.toString()}`}>
            {actor}
        </LinkWithMagnifier>
    );
};

type CollapsibleRowProps = {
    children: ReactNode;
    info: ReactNode;
    canShowInfo?: boolean;
};

const CollapsibleRow = ({children, info, canShowInfo = true}: CollapsibleRowProps) => {
    const [open, setOpen] = useState(false);

    return (
        <>
            <TableRow sx={{"& > *": {borderBottom: "unset !important"}}}>
                <TableCell>
                    {canShowInfo && (
                        <IconButton onClick={() => setOpen((prev) => !prev)}>
                            <FontAwesomeIcon
                                size="xs"
                                icon={open ? "chevron-up" : "chevron-down"}
                                style={{cursor: "pointer"}}
                            />
                        </IconButton>
                    )}
                </TableCell>
                {children}
            </TableRow>
            <TableRow>
                <TableCell colSpan={6} sx={{paddingTop: 0, paddingBottom: 0}}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{margin: 1}}>{info}</Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const ItemDetails = ({details}: {details: any}) => (
    <div>
        <Typography variant="h5" gutterBottom>
            Details
        </Typography>
        <JsonView
            value={details}
            indentWidth={24}
            collapsed={1}
            displayDataTypes={false}
            enableClipboard={false}
            displayObjectSize={false}
            style={{
                fontSize: "1em",
            }}
        />
    </div>
);

const ItemsBody = ({items}: {items: AuditLogsQueryResult["data"]["auditLogs"]["items"]}) =>
    items.map((item) => {
        const details = JSON.parse(item.details);
        return (
            <CollapsibleRow
                key={item.id}
                info={<ItemDetails details={details} />}
                canShowInfo={Object.keys(details).length > 0}
            >
                <TableCell>
                    {DateTime.fromISO(item.timestamp).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                </TableCell>
                <TableCell>
                    <ActorLink actor={item.actor} />
                </TableCell>
                <TableCell>
                    {/* eslint-disable-next-line formatjs/enforce-id, formatjs/enforce-default-message */}
                    <FormattedMessage defaultMessage={item.change} id={`audit.change.${item.change}`} />
                </TableCell>
                <TableCell>
                    <DomainName name={item.domain?.name} id={item.domain?.id} />
                </TableCell>
                <TableCell>
                    <ItemName shortNumber={item.number?.shortNumber} e164={item.number?.e164} />
                </TableCell>
                <TableCell>
                    <ItemName shortNumber={item.relatedNumber?.shortNumber} e164={item.relatedNumber?.e164} />
                </TableCell>
            </CollapsibleRow>
        );
    });

const AuditLogsPage = ({domainId: initialDomainId}: {domainId?: number}) => {
    const [systemEventsOnly, setSystemEventsOnly] = useState(false);
    const [numberOrActorFilterDisplayValue, numberOrActorFilter, setNumberOrActorFilter] = useDebounceState<
        string | undefined
    >(undefined, 400);
    const {gqlOrder, order, setOrder} = useSortingWithCompatibility(["~timestamp"]);

    const {query, totalCount, page, pageSize, setPage, setPageSize} = usePaginatedQuery(
        useAuditLogsQuery,
        {
            variables: {
                sortBy: gqlOrder,
                domainId: initialDomainId === undefined ? (systemEventsOnly ? null : undefined) : initialDomainId,
                numberOrActorFilter,
            },
            fetchPolicy: "no-cache",
        },
        "auditLogs",
    );

    return (
        <TableContainer>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    alignItems: "normal",
                    marginBottom: 2,
                    marginTop: 2,
                }}
            >
                <NumberOrActorFilter
                    value={numberOrActorFilterDisplayValue}
                    onChange={(value) => {
                        setNumberOrActorFilter(value, () => setPage(0));
                    }}
                />
                {initialDomainId === undefined && (
                    <ToggleSystemWideFilter
                        checked={systemEventsOnly}
                        onChange={(checked) => {
                            setSystemEventsOnly(checked);
                            setPage(0);
                        }}
                    />
                )}
            </Box>
            <Table>
                <HeaderWithOrdering
                    fields={[
                        [
                            {path: "", label: undefined},
                            {
                                path: "timestamp",
                                label: defineMessage({id: "audit.headers.timestamp", defaultMessage: "Timestamp"}),
                            },
                            {path: "actor", label: defineMessage({id: "audit.headers.actor", defaultMessage: "Actor"})},
                            {
                                path: "change",
                                label: defineMessage({id: "audit.headers.change", defaultMessage: "Change"}),
                            },
                            {
                                path: "domain_name",
                                label: defineMessage({id: "audit.headers.domain_name", defaultMessage: "$domain_name"}),
                            },
                            {
                                path: "short_number_e164",
                                label: defineMessage({
                                    id: "audit.headers.short_number_e164",
                                    defaultMessage: "Short number (E.164)",
                                }),
                            },
                            {
                                path: "related_short_number_e164",
                                label: defineMessage({
                                    id: "audit.headers.related_short_number_e164",
                                    defaultMessage: "Related short number (E.164)",
                                }),
                            },
                        ],
                    ]}
                    multipleOrdering={false}
                    order={order}
                    orderableFields={["timestamp"]}
                    setOrder={setOrder}
                />
                <TableBody>
                    {query.loading ? <LoadingIcon /> : <ItemsBody items={query.data.auditLogs.items} />}
                </TableBody>
            </Table>
            <div style={{display: "flex", justifyContent: "end"}}>
                <GenericPaginator
                    setPage={setPage}
                    setRowsPerPage={setPageSize}
                    page={page}
                    rowsPerPage={pageSize}
                    totalCount={totalCount}
                />
            </div>
        </TableContainer>
    );
};

export default AuditLogsPage;
